import React, { useState, useMemo, useEffect } from 'react';
import Select from 'react-select';
import { useTable, usePagination, useGlobalFilter } from 'react-table';
import * as XLSX from 'xlsx';
import { formatToZMW, getAllConstituencies, rootUrl, sumOfArray } from '../../helpers';
import { FaArrowCircleLeft, FaArrowCircleRight, FaEdit, FaFileExcel, FaFolderOpen, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Spinner } from '@material-tailwind/react';
import { zambiaProvinces } from '../../data/locations';
import FormGenerator from '../../components/formGenerator';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/auth-context';

    let projId = ''
    let objects = {}

export default function DataTable({ data, coa, depts, progarms, subPrograms, output, units, councils, onDelete, onEdit }) {
    const [filters, setFilters] = useState({
        dept: null,
        program: null,
        councilId: null,
        coa: null,
    });

    // Change year filter to use 'year' property
    const [yearFilter, setYearFilter] = useState(null);
    
    
      const [addButton, setAddButton] = useState(false);
      const [showSlidingDiv, setShowSlidingDiv] = useState(false);
      const [budgetOutput, setBudgetOutput] = useState('');
      
      const {allData, setAllData} = useOutletContext();
      const auth = useAuth();
      
      
       const  constituencies  = getAllConstituencies(zambiaProvinces);

    const openSlidingDiv = () => {
        setShowSlidingDiv(true);
      };
    
      const closeSlidingDiv = () => {
        setShowSlidingDiv(false);
      };
    

    // Custom filter function for global search
    function globalFilterFunction(rows, columnIds, filterValue) {
        return rows.filter(row => {
            return columnIds.some(columnId => {
                const cellValue = row.values[columnId];
                return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase());
            });
        });
    }

    const columns = useMemo(
        () => [
            {
                Header: 'Department',
                accessor: 'dept',
                Cell: ({ value }) => {
                    const department = depts.find((dept) => dept.id === value);
                    return department ? department.name : 'Unknown Department';
                },
            },
            {
                Header: 'Program',
                accessor: 'program',
                Cell: ({ value }) => {
                    const program = progarms.find((p) => p.programCode === value);
                    return program ? program.name : 'Unknown Program';
                },
            },
            {
                Header: 'Subprogram',
                Cell: ({ row }) => {
                    const sub = subPrograms.find((p) => parseInt(p.subProgramCode) 
                    === parseInt(row.original.subprogram) && parseInt(p.programCode) === parseInt(row.original.program));
                    return sub ? sub.name : 'Unknown Subprogram';
                },
            },
            {
                Header: 'COA',
                accessor: 'coa',
                Cell: ({ value }) => {
                    const coas = coa.find((c) => c.account == value);
                    return coas ? `${coas.name} ${coas.account}` : 'Unknown COA';
                },
            },
            {
                Header: 'Output indicator',
                accessor: 'output',
                
            },
            {
                Header: 'Unit',
                accessor: 'unit',
               
            },
            {
                Header: '(TOI)',
                accessor: 'toy',
            },
            {
                Header: 'Debit',
                Cell: ({ row }) => {
                    const isDebit = row.original.coa && row.original.coa.toString().startsWith('1');
                    const amount = parseFloat(row.original.price) * parseFloat(row.original.qty) * parseFloat(row.original.frequency);
                    return isDebit ? formatToZMW(amount) : '';
                },
                Footer: (info) => {
                    const total = useMemo(
                        () =>
                            info.rows.reduce((sum, row) => {
                                const isDebit = row.original.coa && row.original.coa.toString().startsWith('1');
                                const amount = parseFloat(row.original.price) * parseFloat(row.original.qty) * parseFloat(row.original.frequency);
                                return isDebit ? sum + amount : sum;
                            }, 0),
                        [info.rows]
                    );
                    return `Total Debit: ${formatToZMW(total.toFixed(2))}`;
                },
            },
            {
                Header: 'Credit',
                Cell: ({ row }) => {
                    const isCredit = !(row.original.coa && row.original.coa.toString().startsWith('1'));
                    const amount = parseFloat(row.original.price) * parseFloat(row.original.qty) * parseFloat(row.original.frequency);
                    return isCredit ? formatToZMW(amount) : '';
                },
                Footer: (info) => {
                    const total = useMemo(
                        () =>
                            info.rows.reduce((sum, row) => {
                                const isCredit = !(row.original.coa && row.original.coa.toString().startsWith('1'));
                                const amount = parseFloat(row.original.price) * parseFloat(row.original.qty) * parseFloat(row.original.frequency);
                                return isCredit ? sum + amount : sum;
                            }, 0),
                        [info.rows]
                    );
                    return `Total Credit: ${formatToZMW(total.toFixed(2))}`;
                },
            },
            
            {
                Header: 'Measure',
                accessor: 'measure',
            },
            {
                Header: 'Action',
                Cell: ({ row }) => {
                    return (
                        <div className="flex items-center space-x-3" onClick={(e)=>e.stopPropagation()}>
                            <FaEdit
                                title="Edit"
                                onClick={() => onEdit && onEdit(row.original)}
                                className='text-blue-600 cursor-pointer'
                            />
                            <FaTrash
                                title="Delete"
                                onClick={() => onDelete && onDelete(row.original[0])}
                                className='text-red-600 cursor-pointer'
                            />
                        </div>
                    );
                },
            },
        ],
        [coa, councils, depts, output, progarms, subPrograms, units]
    );

    

    

    const filteredData = useMemo(() => {
        return data.filter((item) => {
            return (
                (!filters.dept || item.dept === filters.dept) &&
                (!filters.program || item.program === filters.program) &&
                (!filters.councilId || item.councilId === filters.councilId) &&
                (!filters.coa || item.coa.startsWith(filters.coa)) &&
                (!yearFilter || item.year === yearFilter)
            );
        });
    }, [data, filters, yearFilter]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setGlobalFilter,
        state: { pageIndex, globalFilter },
    } = useTable(
        {
            columns,
            data: filteredData,
            initialState: { pageIndex: 0 },
            globalFilter: globalFilterFunction, // Use the custom global filter function
        },
        useGlobalFilter,
        usePagination
    );

    const handleFilterChange = (name) => (selectedOption) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [name]: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleCoaFilter = () => (selectedOption) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            coa: selectedOption ? selectedOption.value : null,
        }));
    };

    

    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(
            filteredData.map((item) => {
                const isDebit = item.coa && item.coa.toString().startsWith('1');
                const amount = parseFloat(item.price) * parseFloat(item.qty) * parseFloat(item.frequency);
                return {
                    CouncilCode: councils.find((d) => d.code === item.councilId)?.code || 'Unknown',
                    CouncilName: councils.find((d) => d.code === item.councilId)?.name || 'Unknown',
                    DepartmentCode: depts.find((d) => d.id === item.dept)?.id || 'Unknown',
                    DepartmentName: depts.find((d) => d.id === item.dept)?.name || 'Unknown',
                    ProgrammeCode: progarms.find((d) => d.programCode === item.program)?.programCode || 'Unknown',
                    ProgrammeName: progarms.find((p) => p.programCode === item.program)?.name || 'Unknown',
                    SubProgrammeCode: subPrograms.find((s) => parseInt(s.subProgramCode) === parseInt(item.subprogram))?.subProgramCode || 'Unknown',
                    SubProgrammeName: subPrograms.find((s) => parseInt(s.subProgramCode) === parseInt(item.subprogram) && s.programCode === item.program)?.name || 'Unknown',
                    ChartOfAccountCode: coa.find((c) => c.account === item.coa)?.account || 'Unknown',
                    ChartOfAccountName: coa.find((c) => c.account === item.coa)?.name || 'Unknown',
                    KeyIndicator: output.find((o) =>o.indicators === item.output)?.keyIndicators|| 'Unknown',
                    OutputIndicator: item.output || 'Unknown',
                    TOI: item.toy || 'Unknown',
                    Unit: item.unit || 'Unknown',
                    Description: item.description || 'Unknown',
                    qty: item.qty || 'Unknown',
                    frequency: item.frequency || 'Unknown',
                    price: item.price || 'Unknown',
                    Year: item.year || 'Unknown',
                    Debit: isDebit ? amount : '',
                    Credit: !isDebit ? amount : '',
                    objective: item.objective || 'Unknown',
                    measure: item.measure || 'Unknown',
                };
            })
        );

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Data');
        XLSX.writeFile(wb, 'data.xlsx');
    };


    const [lbj, setLbj] = useState({})
    

   async function viewBudge(obj){

        objects = obj
        projId = obj.id

        
        setLbj(obj)
        openSlidingDiv()
        setBudgetOutput(<Spinner color="blue" size="xl" />)
        try{
            const resp = await axios.get(rootUrl(`/getProj/${obj[0]}`))
            setBudgetOutput(respData(obj, resp.data.data))
        }catch(e){
            toast.error(e?.message)
        }

    }

    async function onsubmit(formData){

        formData['uid'] = auth.user.id
        formData['b_id'] = lbj[0]

        try{
            const sponse = await axios.post(rootUrl('/addProj'), formData)
            toast.success('Project added successfully')
            const resp = await axios.get(rootUrl(`/getProj/${lbj[0]}`))
            setBudgetOutput(respData(lbj, resp.data.data))
            setAddButton(false)
        }catch(e){
            toast.error(e?.message)
        }
          
      }

    function respData(wanted, project) {
        return (
          <div className="bg-white shadow-md rounded-md p-4">
            {/* Details Section */}
            <div className="mb-6">
              <table className="table-fixed w-full">
                <tbody>
                  <tr className="border-b">
                    <th className="p-2 text-left text-gray-600 w-1/4">Department</th>
                    <td className="p-2 text-gray-800">
                      {depts.find((d) => d.id === wanted.dept)?.name || ""}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <th className="p-2 text-left text-gray-600">Program</th>
                    <td className="p-2 text-gray-800">
                      {progarms.find((p) => p.programCode === wanted.program)?.name || ""}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <th className="p-2 text-left text-gray-600">COA</th>
                    <td className="p-2 text-gray-800">
                      {coa.find((c) => c.account === wanted.coa)?.name || ""}
                    </td>
                  </tr>
                  <tr>
                    <th className="p-2 text-left text-gray-600">Amount</th>
                    <td className="p-2 text-gray-800 font-semibold">
                      {formatToZMW(
                        parseFloat(wanted.price) *
                          parseFloat(wanted.qty) *
                          parseFloat(wanted.frequency)
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
      
            {/* Projects Table */}
            <hr className="my-4" />
            <div>
              <h3 className="text-lg font-semibold mb-4">Projects</h3>
              <table className="table-auto w-full border-collapse border border-gray-300 rounded-md overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left">#</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Name</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Head</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Sub head</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Description</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {project && project.length > 0 ? (
                    project.map((proj, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-4 py-2">{index + 1}</td>
                        <td className="border border-gray-300 px-4 py-2">{proj.name}</td>
                        <td className="border border-gray-300 px-4 py-2">{proj.head}</td>
                        <td className="border border-gray-300 px-4 py-2">{proj.sub}</td>
                        <td className="border border-gray-300 px-4 py-2">{proj.description}</td>
                        <td className="border border-gray-300 px-4 py-2 font-semibold">
                          {formatToZMW(proj.amount)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="border border-gray-300 px-4 py-6 text-center">
                        <div className="flex flex-col justify-center items-center">
                          <FaFolderOpen className="text-gray-500 text-6xl mb-2" />
                          <p className="text-gray-600">No projects found</p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {
                    project.length > 0 ? (
                        <tr> 
                        <th colSpan={5}>Total</th>
                        <th>{formatToZMW(sumOfArray(project.map((res)=>parseFloat(res.amount))))}</th>
                      </tr>
                    ):null
                  }
                 
                </tbody>
              </table>
            </div>
          </div>
        );
      }

     
      const [fields, setFields] = useState([]);

      useEffect(() => {
        if (allData.head !== undefined) {
          setFields([
            {
              type: 'select',
              name: 'constituency',
              label: 'Constituency',
              required: true,
              options: constituencies.map(res => ({
                value: res,
                label: res,
              })),
            },
            {
              type: 'select',
              name: 'head',
              label: 'Head',
              required: true,
              dependentFields: ['sub'],
              options: allData.head.map(res => ({
                value: res.id,
                label: res.name,
              })),
            },
            {
              type: 'select',
              name: 'sub',
              label: 'Sub',
              required: true,
              options: allData.sub.map(res => ({
                value: res.id,
                label: res.name,
                dependsOn: 'head',
                dependentValue: res.pid,
              })),
            },
            { type: 'text', name: 'name', label: 'Name', required: true },
            { type: 'number', name: 'amount', label: 'Amount', required: true },
            { type: 'textarea', name: 'description', label: 'Description' },
          ]);
        }
      }, [allData]);
      
      
      

    return (
        <>
            {/* Filters */}
            <div className="flex flex-col md:flex-row md:flex-wrap gap-3 mb-4">
                <Select
                    placeholder="Filter by Department"
                    options={[...new Set(data.map((item) => item.dept))].map((dept) => ({
                        value: dept,
                        label: depts.find((a) => a.id === dept)?.name || 'Unknown Department',
                    }))}
                    onChange={handleFilterChange('dept')}
                    className="w-full md:w-56 text-sm"
                />
                <Select
                    placeholder="Filter by Program"
                    options={[...new Set(data.map((item) => item.program))].map((program) => ({
                        value: program,
                        label: progarms.find((p) => p.programCode === program)?.name || 'Unknown Program',
                    }))}
                    onChange={handleFilterChange('program')}
                    className="w-full md:w-56 text-sm"
                />
                
                <Select
                    placeholder="Filter by COA"
                    options={[...new Set(data.map((item) => item.coa))].map((co) => ({
                        value: co,
                        label: coa.find((c) => c.account == co)?.name || 'Unknown COA',
                    }))}
                    onChange={handleFilterChange('coa')}
                    className="w-full md:w-56 text-sm"
                />

                <Select
                    placeholder="IType"
                    options={[
                        {value:1, label:'Revenue'},
                        {value:2, label:'Expense'},
                        {value:3, label:'Assets'},
                        {value:4, label:'Liabilities'},
                        {value:5, label:'Equity'},
                        {value:6, label:'Reserves'},

                    ]}
                    onChange={handleCoaFilter()}
                    className="w-full md:w-40 text-sm"
                />
                <Select
                    placeholder="Filter by Year"
                    options={Array.from(new Set(data.map((item) => item.year))).map((year) => ({
                        value: year,
                        label: year,
                    }))}
                    onChange={(selectedOption) => setYearFilter(selectedOption ? selectedOption.value : null)}
                    isClearable
                    className="w-full md:w-40 text-sm"
                />
                <button
                    onClick={exportToExcel}
                    className="inline-flex items-center rounded-lg bg-blue-950 px-3 py-2 text-xs md:text-sm font-semibold text-white hover:bg-blue-900"
                >
                    <FaFileExcel className="mr-1" />
                    Export
                </button>
            </div>

            {/* Table */}
            <div className="rounded-xl border border-gray-100 bg-white overflow-hidden mt-3">
              <div className="overflow-x-auto">
                <table {...getTableProps()} className="min-w-full text-sm">
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th
                                        {...column.getHeaderProps()}
                                        className="px-4 md:px-6 py-3 border-b-2 border-gray-200 bg-blue-950 text-left text-[11px] md:text-xs font-semibold text-white uppercase tracking-wider"
                                    >
                                        {column.render('Header')}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                            prepareRow(row);
                            return (
                                <tr 
                                  className="cursor-pointer hover:bg-gray-50"
                                  onClick={()=>viewBudge(row.original)}
                                  {...row.getRowProps()}
                                >
                                    {row.cells.map((cell) => (
                                        <td
                                            {...cell.getCellProps()}
                                            className="px-4 md:px-6 py-3 border-b border-gray-200 align-top"
                                        >
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        {headerGroups.map((headerGroup) => (
                            <tr
                                className="bg-gray-100"
                                {...headerGroup.getFooterGroupProps()}
                            >
                                {headerGroup.headers.map((column) => (
                                    <td
                                        {...column.getFooterProps()}
                                        className="px-4 md:px-6 py-2 border-t border-gray-200 text-[11px] md:text-xs font-medium text-gray-700 uppercase tracking-wider"
                                    >
                                        {column.render('Footer')}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {/* Variance row */}
                        <tr className="bg-gray-200 font-bold">
                            <td colSpan={headerGroups[0].headers.length - 3}></td>
                            <td className="px-4 md:px-6 py-2 border-t border-gray-200 text-[11px] md:text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Variance
                            </td>
                            <td
                              className="px-4 md:px-6 py-2 border-t border-gray-200 text-[11px] md:text-xs font-medium text-gray-700 uppercase tracking-wider"
                              colSpan={2}
                            >
                                {(() => {
                                    const totalDebit = filteredData.reduce((sum, item) => {
                                        const isDebit = item.coa && item.coa.toString().startsWith('1');
                                        const amount = parseFloat(item.price) * parseFloat(item.qty) * parseFloat(item.frequency);
                                        return isDebit ? sum + amount : sum;
                                    }, 0);
                                    const totalCredit = filteredData.reduce((sum, item) => {
                                        const isCredit = !(item.coa && item.coa.toString().startsWith('1'));
                                        const amount = parseFloat(item.price) * parseFloat(item.qty) * parseFloat(item.frequency);
                                        return isCredit ? sum + amount : sum;
                                    }, 0);
                                    return formatToZMW(totalDebit - totalCredit);
                                })()}
                            </td>
                        </tr>
                    </tfoot>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="pagination mt-4 flex items-center justify-between text-xs md:text-sm">
                <div className="flex items-center gap-2">
                    <button
                      onClick={() => previousPage()}
                      disabled={!canPreviousPage}
                      className="disabled:opacity-40"
                    >
                      <FaArrowCircleLeft className="text-blue-950 mr-1" size={24}/>
                    </button>
                    <button
                      onClick={() => nextPage()}
                      disabled={!canNextPage}
                      className="disabled:opacity-40"
                    >
                      <FaArrowCircleRight size={24} className="text-blue-950 mr-1"/>
                    </button>
                </div>
                <span>
                    Page{" "}
                    <strong>
                        {pageIndex + 1} of {pageOptions.length}
                    </strong>
                </span>
            </div>

            {/* Sliding budget details panel */}
            {showSlidingDiv && (
              <div className="fixed inset-0 z-40 flex justify-end">
                <div
                  className="flex-1 bg-black/40"
                  onClick={closeSlidingDiv}
                />
                <div
                  id="slidingDiv"
                  className="bg-white h-full w-full sm:w-4/5 lg:w-2/3 xl:w-1/2 shadow-2xl relative flex flex-col"
                >
                  <div className="flex items-center justify-between p-3 border-b border-gray-200">
                    <h3 className="m-0 font-semibold text-sm md:text-lg text-gray-800">
                      Budget Details
                    </h3>
                    <FaTimes 
                      onClick={closeSlidingDiv}
                      className="p-1 text-xl text-red-500 cursor-pointer rounded-full hover:bg-red-50"
                    />
                  </div>
                  <div className="h-[90vh] overflow-y-auto p-4">
                    {budgetOutput}

                    <button
                      onClick={()=>setAddButton(!addButton)}
                      className="flex items-center bg-blue-950 px-3 py-2 text-white rounded-md my-4 text-xs md:text-sm hover:bg-blue-900"
                    >
                      <FaPlus className="mr-1 text-white"/> Add Project
                    </button>

                    {addButton && (
                      <FormGenerator fields={fields} onSubmit={onsubmit} />
                    )}
                  </div>
                </div>
              </div>
            )}
        </>
    );
}
